import {
    createDeserializer,
    handleJsonApiError,
    injectPageParams,
    type PageParams,
} from "@jsonapi-serde/client";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod/mini";
import { zj } from "zod-joda/mini";
import { reviewRequestAttributesSchema } from "@/queries/review-request.js";
import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";

export const reviewAttributesSchema = z.object({
    createdAt: z.pipe(
        zj.zonedDateTime(),
        z.transform((value) => value.withZoneSameInstant(localZoneId)),
    ),
    rating: z.int().check(z.gte(1), z.lte(5)),
    comment: z.string(),
    wouldRecommend: z.boolean(),
    publicSharingAllowed: z.boolean(),
    referralOptIn: z.boolean(),
});

const deserializeReviews = createDeserializer({
    type: "review",
    cardinality: "many",
    attributesSchema: reviewAttributesSchema,
    relationships: {
        request: {
            type: "review_request",
            cardinality: "one",
            included: {
                attributesSchema: reviewRequestAttributesSchema,
            },
        },
    },
});

export type Review = ReturnType<typeof deserializeReviews>["data"][number];
export type PaginatedReviews = ReturnType<typeof deserializeReviews>;

export type ListReviewsOptions = {
    pageParams?: PageParams;
};

export const createReviewQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListReviewsOptions) =>
        queryOptions({
            queryKey: ["reviews", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/reviews");
                injectPageParams(url, options?.pageParams);

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return deserializeReviews(await response.json());
            },
        }),
});
